import { create } from "zustand";
import type { ToastData } from "@safers/ui/molecules";

type ToastVariant = ToastData["variant"];
type ToastInput = Omit<ToastData, "id">;

interface ToastFunction {
  (input: ToastInput | string): string;
  success: (input: ToastInput | string) => string;
  error: (input: ToastInput | string) => string;
  warning: (input: ToastInput | string) => string;
  info: (input: ToastInput | string) => string;
}

interface ToastState {
  toasts: ToastData[];
  dismissToast: (id: string) => void;
  toast: ToastFunction;
}

const TOAST_LIMIT = 5;
const DEFAULT_DURATION = 5000;

let counter = 0;
const genId = () => `toast-${++counter}-${Date.now()}`;

export const useToastStore = create<ToastState>((set) => {
  const push = (input: ToastInput): string => {
    const id = genId();
    const next: ToastData = { id, duration: DEFAULT_DURATION, ...input };
    set((s) => ({ toasts: [next, ...s.toasts].slice(0, TOAST_LIMIT) }));
    return id;
  };

  const fn =
    (variant?: ToastVariant) =>
    (input: ToastInput | string): string =>
      typeof input === "string"
        ? push({ description: input, variant })
        : push({ ...input, variant });

  const toast = Object.assign(fn(), {
    success: fn("success"),
    error: fn("error"),
    warning: fn("warning"),
    info: fn("info"),
  }) as ToastFunction;

  return {
    toasts: [],
    dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    toast,
  };
});

export const selectToasts = (s: ToastState) => s.toasts;
export const selectDismissToast = (s: ToastState) => s.dismissToast;
